// Layer: presentation. Chooses which playbook the editor is editing.
//
// Only one playbook used to be reachable. The console was handed a single hardcoded
// graph, `pre-arrival-room-readiness`, and the Playbooks tab rendered the editor on it
// directly, so `in-stay-recovery-flag@v1` and `in-stay-fb-upsell@v1` existed in the API,
// were seeded on the demo property, and were cited by name in the Guest journey's
// reasoning lines, while there was no control anywhere that could open either of them.
//
// The list comes from the API, which has had `GET /v1/properties/{id}/playbooks` all
// along. That matters beyond the selector: a playbook read from the API carries its real
// id and version, so saving it stores version N+1 of that playbook. The hardcoded seed
// carried a placeholder id under a name the property had already used, which is the one
// input `PlaybookNameTaken` rejects, so saving an unmodified console seed against a
// seeded property was a 409.
import { useCallback, useEffect, useState } from "react";
import type { StoriaApi } from "../application/ports";
import type { Playbook } from "../domain/types";
import { PlaybookEditor } from "./PlaybookEditor";
import { ErrorPanel, messageOf } from "./ShiftView";

interface Props {
  api: StoriaApi;
  propertyId: string;
  /** Shown when the property has no playbooks of its own. */
  fallback: readonly Playbook[];
}

export function PlaybookPanel({ api, propertyId, fallback }: Props) {
  const [playbooks, setPlaybooks] = useState<readonly Playbook[] | null>(null);
  const [stored, setStored] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    if (!propertyId) {
      // Not an error state with a retry button: retrying cannot fix a missing build-time
      // configuration value, and offering the button implies it might.
      setPlaybooks(fallback);
      setStored(false);
      setSelected(fallback[0]?.id ?? "");
      return;
    }
    api
      .loadPlaybooks(propertyId)
      .then((found) => {
        const list = found.length > 0 ? found : fallback;
        setPlaybooks(list);
        setStored(found.length > 0);
        setSelected((current) =>
          list.some((p) => p.id === current) ? current : (list[0]?.id ?? ""),
        );
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [api, propertyId, fallback]);

  useEffect(load, [load]);

  // The landmark is present in every state, so a screen reader still has a main region
  // to navigate to while the list is loading.
  return (
    <main aria-label="Playbooks" className="stack">
      <header className="panel-head">
        <h2>Playbooks</h2>
        <p className="muted">
          {playbooks ? `${playbooks.length} on this property` : "Loading"}
        </p>
      </header>

      {error ? (
        <ErrorPanel message={error} onRetry={load} />
      ) : !playbooks ? (
        <p className="muted" role="status">Loading playbooks…</p>
      ) : playbooks.length === 0 ? (
        <p className="muted">No playbooks on this property yet.</p>
      ) : (
        <>
          <div className="card">
            <label htmlFor="playbook-picker">Playbook</label>
            <select
              id="playbook-picker"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {playbooks.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}@v{p.version}
                </option>
              ))}
            </select>
            {!stored ? (
              <p className="muted">
                These are the pilot seeds, not this property's own playbooks. Saving is
                off until the property has been seeded, because a seed carries a
                placeholder id and the API would reject it as a duplicate name.
              </p>
            ) : null}
          </div>

          {/* Keyed on the selection so switching playbooks resets the editor's own
              state. Without the key the editor keeps the previous graph's unsaved edits
              and its "Saved." notice, both of which would then describe the wrong
              playbook. */}
          {playbooks
            .filter((p) => p.id === selected)
            .map((p) => (
              <PlaybookEditor
                key={p.id}
                initial={p}
                api={api}
                propertyId={propertyId}
                readOnly={!stored}
              />
            ))}
        </>
      )}
    </main>
  );
}
